import { useEffect, useRef, useState } from 'react';
import { SNAP } from './schedule';

/*
  Пороги ожидания (R42, R32): что показывает рамка, пока не может показать содержимое.
  - до SHOW_AFTER — ничего: короткое ожидание глаз не замечает, а мелькнувший знак только тревожит;
  - с SHOW_AFTER — знак по шагам (медальон во вкладке); показавшись, он держится не меньше MIN_VISIBLE —
    без мигания;
  - с NAME_AFTER — строка шага, с COUNT_AFTER — счётчик секунд, с SLOW_AFTER — «Дольше обычного» и действия.
  На «готово» — досадка (SNAP): содержимое ждёт её, не дольше 160 мс, если знак виден уже достаточно; потом
  знак уходит за LEAVE_MS, а содержимое входит. Только таймеры и тик раз в секунду — никаких покадровых
  обновлений.
*/

export const SHOW_AFTER = 400;
/** = duration-slow. */
export const MIN_VISIBLE = 480;
export const NAME_AFTER = 1500;
export const COUNT_AFTER = 3000;
export const SLOW_AFTER = 10000;
/** Знак уходит (animate-fade-out = duration-fast); содержимое входит animate-fade-in. */
export const LEAVE_MS = 120;

export interface LoadingGate {
  /** Знак виден: с порога и до конца досадки. */
  shown: boolean;
  /** Пора назвать шаг. */
  named: boolean;
  /** Пора считать секунды. */
  counting: boolean;
  /** Дольше обычного: пора предложить действия. */
  slow: boolean;
  /** Сколько секунд идёт ожидание (обновляется, пока идёт счёт). */
  seconds: number;
  /** Ожидание кончилось, знак досаживает веер перед уходом. */
  finishing: boolean;
}

const IDLE: LoadingGate = {
  shown: false,
  named: false,
  counting: false,
  slow: false,
  seconds: 0,
  finishing: false,
};

/**
 * Пороги ожидания рамки. pending — рамка ждёт; startedAt — когда ожидание началось на самом деле
 * (performance.now(): для сцены первой загрузки — начало страницы), по умолчанию — когда pending стал true.
 * attempt — номер попытки: «Повторить» начинает ожидание заново — пороги с нуля от нажатия, а знак остаётся
 * на экране (кнопка была на нём).
 */
export function useLoadingGate(pending: boolean, startedAt?: number, attempt = 0): LoadingGate {
  const [gate, setGate] = useState({ ...IDLE, pending, attempt });
  // Смена ожидания — правкой состояния при отрисовке, без лишнего кадра. Началось заново — пороги с нуля,
  // а уже видимый знак не пропадает; кончилось — видимый знак досаживает веер, невидимый так и не появится.
  if (gate.pending !== pending || gate.attempt !== attempt) {
    setGate(
      pending
        ? { ...IDLE, pending, attempt, shown: gate.shown || gate.attempt !== attempt }
        : gate.shown
          ? { ...gate, pending, attempt, finishing: true }
          : { ...IDLE, pending, attempt },
    );
  }

  useEffect(() => {
    if (!pending) return undefined;
    const t0 = attempt === 0 && startedAt !== undefined ? startedAt : performance.now();
    const left = (ms: number) => Math.max(0, t0 + ms - performance.now());
    const seconds = () => Math.floor((performance.now() - t0) / 1000);
    let tick = 0;
    const timers = [
      window.setTimeout(() => setGate((g) => ({ ...g, shown: true })), left(SHOW_AFTER)),
      window.setTimeout(() => setGate((g) => ({ ...g, named: true })), left(NAME_AFTER)),
      window.setTimeout(() => {
        setGate((g) => ({ ...g, counting: true, seconds: seconds() }));
        tick = window.setInterval(() => setGate((g) => ({ ...g, seconds: seconds() })), 1000);
      }, left(COUNT_AFTER)),
      window.setTimeout(() => setGate((g) => ({ ...g, slow: true })), left(SLOW_AFTER)),
    ];
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      window.clearInterval(tick);
    };
  }, [pending, startedAt, attempt]);

  // Когда знак показался: от этого считается MIN_VISIBLE.
  const shownAt = useRef(0);
  useEffect(() => {
    if (gate.shown) shownAt.current = performance.now();
  }, [gate.shown]);

  useEffect(() => {
    if (!gate.finishing) return undefined;
    const wait = Math.max(SNAP.ms, MIN_VISIBLE - (performance.now() - shownAt.current));
    const t = window.setTimeout(
      () => setGate((g) => (g.pending ? g : { ...IDLE, pending: false, attempt: g.attempt })),
      wait,
    );
    return () => window.clearTimeout(t);
  }, [gate.finishing]);

  return {
    shown: gate.shown,
    named: gate.named,
    counting: gate.counting,
    slow: gate.slow,
    seconds: gate.seconds,
    finishing: gate.finishing,
  };
}
